/**
 * Centralized localStorage Manager
 * Verwaltet alle localStorage-Operationen zentral
 * Verhindert Duplikate, Race Conditions und Inkonsistenzen
 */

import autoSaveService from './autoSaveService';
import {
  businessDataStorageKey,
  getCurrentBusinessId as getSharedCurrentBusinessId,
  normalizeBusinessStorageId,
  scoreBusinessMergedPayload
} from '../utils/sharedCore';
import { getStorageEngine, getBusinessPayloadRepository } from '../app/di/container';
import { KV_COLLECTION } from '../infrastructure/storage/StorageEngine';
import { unwrapCompressionEnvelopeValue } from '../utils/localStorageCompression.js';
import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import {
  entityFieldNeedsSync,
  entityBatchFieldsNeedSync
} from '../infrastructure/storage/entityFieldSyncCheck';
import { isEntityShellBlob } from '../utils/sharedCore';
import { isLegacyStandaloneDomainKey } from '../utils/localStorageHealth';

class LocalStorageManager {
  constructor() {
    this.locks = new Map(); // Locks für atomare Operationen
    this.cache = new Map(); // Cache für häufige Leseoperationen
    this.pendingWrites = new Map(); // Queue für Schreiboperationen
    this.isProcessing = false;
    this._payloadRepo = null;
    
    // Standard Key-Patterns
    this.KEY_PATTERNS = {
      BUSINESS_DATA: (id) => businessDataStorageKey(id),
      CURRENT_USER: 'currentUser',
      CURRENT_BUSINESS: 'currentBusiness',
      CURRENT_ROLE: (id) => `currentRole_${id}`,
      WORKER_SESSIONS: (businessId, workerId) => `worker_sessions_${businessId}_${workerId}`,
      MANAGER_CODE: 'managerCode',
      APP_LANGUAGE: 'appLanguage',
      APP_CURRENCY: 'appCurrency',
      OFFLINE_BUSINESSES: 'offline_businesses',
      OFFLINE_USERS: 'offline_users'
    };
    
    // Berechnungs-Locks (verhindert parallele Berechnungen)
    this.calculationLocks = new Map();
    
    // Cache-TTL (5 Sekunden)
    this.CACHE_TTL = 5000;
  }

  _getPayloadRepository() {
    if (!this._payloadRepo) {
      this._payloadRepo = getBusinessPayloadRepository();
    }
    return this._payloadRepo;
  }

  /**
   * Hole Business-ID (konsistent)
   */
  getCurrentBusinessId() {
    try {
      return (
        getSharedCurrentBusinessId({ allowFallback: false }) ||
        getSharedCurrentBusinessId({ allowFallback: true })
      );
    } catch (error) {
      console.error('Error getting business ID:', error);
      return null;
    }
  }

  /**
   * Lese aus localStorage (mit Cache)
   */
  get(key, useCache = true) {
    // Cache-Check
    if (useCache && this.cache.has(key)) {
      const cached = this.cache.get(key);
      if (Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.value;
      }
      this.cache.delete(key);
    }
    
    try {
      const engine = getStorageEngine();
      const value = engine.get(KV_COLLECTION, key);
      if (value === undefined || value === null) return null;

      let parsed;
      if (typeof value === 'string') {
        if (value === '[object Object]' || value === '"[object Object]"' || value.trim() === '') {
          console.warn(`⚠️ [LOCAL STORAGE MANAGER] Corrupted data for key: ${key}, cleaning up`);
          engine.delete(KV_COLLECTION, key);
          return null;
        }
        try {
          parsed = JSON.parse(value);
        } catch (parseError) {
          console.error(`Error parsing ${key}:`, parseError);
          engine.delete(KV_COLLECTION, key);
          return null;
        }
      } else if (typeof value === 'object' && value !== null) {
        parsed = value;
      } else {
        parsed = value;
      }

      parsed = unwrapCompressionEnvelopeValue(key, parsed);

      // Cache speichern
      if (useCache) {
        this.cache.set(key, {
          value: parsed,
          timestamp: Date.now()
        });
      }

      return parsed;
    } catch (error) {
      console.error(`Error reading ${key}:`, error);
      return null;
    }
  }

  /**
   * Schreibe in localStorage (atomar, mit Lock)
   */
  async set(key, value, options = {}) {
    if (isLegacyStandaloneDomainKey(key)) {
      throw new Error(
        `[localStorageManager] Direct write blocked for legacy key "${key}". Use queueBusinessPayloadWrite / field writes.`
      );
    }

    const {
      immediate = false,
      priority = 'normal',
      skipCache = false
    } = options;
    
    // Lock für atomare Operation
    const lockKey = `lock_${key}`;
    if (this.locks.has(lockKey)) {
      // Warte auf Lock (max 1 Sekunde)
      const start = Date.now();
      while (this.locks.has(lockKey) && Date.now() - start < 1000) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    }
    
    this.locks.set(lockKey, true);
    
    try {
      // Verwende Auto-Save Service
      const result = autoSaveService.save(key, value, {
        immediate: immediate || priority === 'high',
        priority
      });
      
      // Cache invalidieren
      if (!skipCache) {
        this.cache.delete(key);
      }
      
      return result;
    } finally {
      this.locks.delete(lockKey);
    }
  }

  /**
   * Lese Business-Daten (konsistent)
   */
  getBusinessData(businessId = null) {
    const id = normalizeBusinessStorageId(businessId || this.getCurrentBusinessId());
    if (!id) return null;

    const canonicalKey = this.KEY_PATTERNS.BUSINESS_DATA(id);
    let data = canonicalKey ? this.get(canonicalKey, false) : null;

    try {
      const repo = this._getPayloadRepository();
      const best = repo.readBest(id);
      const currentScore = scoreBusinessMergedPayload(data);
      if (best.parsed && best.score > currentScore) {
        data = best.parsed;
        if (canonicalKey) {
          try {
            repo.consolidateToCanonical(id);
            this.cache.delete(canonicalKey);
          } catch {
            /* ignore migration failure */
          }
        }
      }
    } catch (e) {
      console.warn('[localStorageManager] readBestBusinessMergedPayload failed:', e?.message || e);
    }

    return data;
  }

  /**
   * Schreibe Business-Daten (atomar)
   */
  async setBusinessData(field, value, businessId = null, options = {}) {
    const id = normalizeBusinessStorageId(businessId || this.getCurrentBusinessId());
    if (!id) {
      console.warn('[localStorageManager] No business ID for setBusinessData — data will not persist', field);
      return false;
    }

    const key = this.KEY_PATTERNS.BUSINESS_DATA(id);

    try {
      const current = this.get(key, false) || {};
      const kvMatches = JSON.stringify(current[field]) === JSON.stringify(value);
      let needsWrite = !kvMatches;

      if (kvMatches && isEntityStoreNoKvBlobEnabled()) {
        needsWrite = await entityFieldNeedsSync(id, field, value);
        if (needsWrite && process.env.NODE_ENV === 'development') {
          console.warn(
            `[localStorageManager] KV/blob matches ${field} but entity_* is out of sync — forcing write`,
            { businessId: id, count: Array.isArray(value) ? value.length : undefined }
          );
        }
      }

      if (!needsWrite) {
        return true;
      }

      await this._getPayloadRepository().setField(id, field, value);
      this.cache.delete(key);
      return true;
    } catch (error) {
      console.error(`[localStorageManager] setBusinessData(${field}) failed:`, error);
      return false;
    }
  }

  /**
   * Batch-Operation (mehrere Felder gleichzeitig)
   */
  async setBusinessDataBatch(updates, businessId = null, options = {}) {
    const id = normalizeBusinessStorageId(businessId || this.getCurrentBusinessId());
    if (!id) return false;

    const key = this.KEY_PATTERNS.BUSINESS_DATA(id);
    const lockKey = `lock_${key}`;

    // Lock
    if (this.locks.has(lockKey)) {
      const start = Date.now();
      while (this.locks.has(lockKey) && Date.now() - start < 1000) {
        await new Promise((resolve) => setTimeout(resolve, 10));
      }
    }

    this.locks.set(lockKey, true);

    try {
      const existingData = this.get(key, false) || {};
      let needsWrite = Object.entries(updates).some(
        ([field, value]) => JSON.stringify(existingData[field]) !== JSON.stringify(value)
      );

      if (!needsWrite && isEntityStoreNoKvBlobEnabled()) {
        const batchDrift = await entityBatchFieldsNeedSync(id, updates);
        needsWrite = batchDrift.needsSync;
        if (needsWrite && process.env.NODE_ENV === 'development') {
          console.warn(
            `[localStorageManager] KV/blob batch matches but entity_* out of sync — forcing write`,
            { businessId: id, field: batchDrift.field }
          );
        }
      }

      if (!needsWrite) {
        return true;
      }

      await this._getPayloadRepository().setFields(id, updates);

      this.cache.delete(key);

      return true;
    } finally {
      this.locks.delete(lockKey);
    }
  }

  /**
   * Berechnungs-Lock (verhindert parallele Berechnungen)
   */
  async acquireCalculationLock(calculationType) {
    const lockKey = `calc_${calculationType}`;
    
    if (this.calculationLocks.has(lockKey)) {
      // Berechnung läuft bereits
      return false;
    }
    
    this.calculationLocks.set(lockKey, Date.now());
    return true;
  }

  /**
   * Berechnungs-Lock freigeben
   */
  releaseCalculationLock(calculationType) {
    const lockKey = `calc_${calculationType}`;
    this.calculationLocks.delete(lockKey);
  }

  /**
   * Prüfe ob Berechnung läuft
   */
  isCalculationRunning(calculationType) {
    const lockKey = `calc_${calculationType}`;
    return this.calculationLocks.has(lockKey);
  }

  /**
   * Cache leeren
   */
  clearCache(key = null) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Alle Keys für ein Business auflisten
   */
  listBusinessKeys(businessId) {
    const engine = getStorageEngine();
    return engine.listKvKeys().filter(
      (key) => key && (key.includes(`business_${businessId}`) || key.includes(`biz:${businessId}`))
    );
  }

  /**
   * Konsistenz-Prüfung
   */
  checkConsistency(businessId) {
    const issues = [];
    const businessData = this.getBusinessData(businessId);
    
    if (!businessData) {
      issues.push('No business data found');
      return issues;
    }

    if (isEntityStoreNoKvBlobEnabled() || isEntityShellBlob(businessData)) {
      return issues;
    }
    
    // Prüfe auf erwartete Felder
    const expectedFields = [
      'products', 'suppliers', 'purchases', 'sales', 
      'customers', 'lots', 'orders', 'invoices'
    ];
    
    expectedFields.forEach(field => {
      if (!(field in businessData)) {
        issues.push(`Missing field: ${field}`);
      } else if (!Array.isArray(businessData[field]) && field !== 'lots') {
        issues.push(`Field ${field} should be array but is ${typeof businessData[field]}`);
      }
    });
    
    // Prüfe lots-Format
    if (businessData.lots) {
      const isArray = Array.isArray(businessData.lots);
      const isObject = typeof businessData.lots === 'object' && !isArray;
      
      if (!isArray && !isObject) {
        issues.push('Lots should be array or object');
      }
    }
    
    return issues;
  }

  /**
   * Bereinige alte/temporäre Keys
   */
  cleanupTemporaryKeys() {
    const tempPatterns = [
      'resumeAddPurchase',
      'resumeAddOrder',
      '_backup_',
      '_temp_',
      'draft_'
    ];
    
    const keysToRemove = [];
    const engine = getStorageEngine();
    for (const key of engine.listKvKeys()) {
      if (key && tempPatterns.some((pattern) => key.includes(pattern))) {
        // Prüfe Alter (älter als 24 Stunden)
        try {
          const data = engine.getRaw(KV_COLLECTION, key);
          if (data) {
            // getItem already parses JSON, but handle both cases
            let parsed;
            if (typeof data === 'string') {
              if (data === '[object Object]' || data === '"[object Object]"' || data.trim() === '') {
                // Corrupted data, remove it
                keysToRemove.push(key);
              } else {
                try {
                  parsed = JSON.parse(data);
                } catch (parseError) {
                  // Invalid JSON, remove it
                  keysToRemove.push(key);
                  parsed = null;
                }
              }
            } else if (typeof data === 'object' && data !== null) {
              parsed = data;
            } else {
              parsed = null;
            }

            if (parsed && parsed.timestamp) {
              const age = Date.now() - new Date(parsed.timestamp).getTime();
              if (age > 24 * 60 * 60 * 1000) {
                // 24 Stunden
                keysToRemove.push(key);
              }
            }
          }
        } catch (e) {
          // Wenn nicht parsbar, entfernen
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => {
      engine.delete(KV_COLLECTION, key);
      this.cache.delete(key);
    });
    
    return keysToRemove.length;
  }
}

export const localStorageManager = new LocalStorageManager();
export default localStorageManager;





